import { AppState } from 'react-native';
import {
  PendingInboundReviewTiming,
  useOrderInboundsStore,
} from '@/stores/order-inbounds-store';
import { useCallback, useEffect, useRef } from 'react';
import { useOrderDrawerContext } from '@/contexts/order-drawer-context';

// Threshold for both the start- and end-time idle adjustments below.
const IDLE_GAP_THRESHOLD_MS = 30_000;

// Minimal shape needed from the screen's navigation prop - just enough to
// tell whether this inbound's screen is the one currently focused.
interface NavigationFocusCheck {
  isFocused: () => boolean;
}

interface Params {
  isProcessing: boolean;
  navigation: NavigationFocusCheck;
  orderUUID: string;
  orderUUIDs: string[];
  pendingOrderUUIDs: string[];
  restaurantUUID?: string;
  supplierUUID?: string;
}

export const useInboundReviewTiming = ({
  isProcessing,
  navigation,
  orderUUID,
  orderUUIDs,
  pendingOrderUUIDs,
  restaurantUUID,
  supplierUUID,
}: Params) => {
  const clickedIntoAtRef = useRef<number>(performance.now());
  const processedAtRef = useRef<number | null>(null);
  const viewedAtRef = useRef<number | null>(null);
  const firstInteractionAtRef = useRef<number | null>(null);
  const lastInteractionAtRef = useRef<number | null>(null);
  const wasProcessingRef = useRef(isProcessing);

  const setPendingReviewTiming = useOrderInboundsStore(
    (state) => state.actions.setPendingReviewTiming,
  );
  // isFocused() alone misses the wide-layout case: the cart is a drawer
  // rendered on top of this same screen, not a navigated-away screen, so
  // isFocused() stays true while it's open.
  const { open: isCartDrawerOpen } = useOrderDrawerContext();

  // Event 1: the inbound was clicked into. Resets timing state for whatever
  // inbound was active before.
  useEffect(() => {
    clickedIntoAtRef.current = performance.now();
    processedAtRef.current = null;
    viewedAtRef.current = null;
    firstInteractionAtRef.current = null;
    lastInteractionAtRef.current = null;
    wasProcessingRef.current = isProcessing;
    // Only the identity of the inbound should reset the session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderUUID]);

  // Event 2: the inbound finished processing while the user was on it.
  useEffect(() => {
    if (wasProcessingRef.current && !isProcessing) {
      processedAtRef.current = performance.now();
      firstInteractionAtRef.current = null;
    }
    wasProcessingRef.current = isProcessing;
  }, [isProcessing]);

  // Event 3: the OA tab/app came into view. AppState shims off
  // visibilitychange on web (see useComponentFocus), so one listener covers
  // both platforms. Gated on focus/drawer state so an app foreground while
  // the user is actually looking at the cart isn't attributed here.
  useEffect(() => {
    const subscription = AppState.addEventListener('change', (nextState) => {
      if (
        nextState === 'active' &&
        navigation.isFocused() &&
        !isCartDrawerOpen
      ) {
        viewedAtRef.current = performance.now();
        firstInteractionAtRef.current = null;
      }
    });
    return () => subscription.remove();
  }, [navigation, isCartDrawerOpen]);

  // Event 4: a click (whitespace included) or scroll in the detail panel.
  // Tracks the first one since the most recent of events 1-3 (start-time
  // override) and the most recent one overall (end-time trim below).
  const recordInteraction = useCallback(() => {
    const now = performance.now();
    if (firstInteractionAtRef.current === null) {
      firstInteractionAtRef.current = now;
    }
    lastInteractionAtRef.current = now;
  }, []);

  // Event 5: the continue button was pressed. Freezes the resolved start
  // time and the inbound-review-specific end time, and stages both for
  // order-summary.tsx to pick up once the order actually submits. Accepts
  // the press timestamp explicitly so it reflects the actual click, not
  // whenever the caller's async validation/submission finishes.
  const stageContinuePress = useCallback(
    (continuePressedAt: number = performance.now()) => {
      let startSource: PendingInboundReviewTiming['startSource'] =
        'clicked_into';
      let startTime = clickedIntoAtRef.current;
      if (
        processedAtRef.current !== null &&
        processedAtRef.current > startTime
      ) {
        startSource = 'processed';
        startTime = processedAtRef.current;
      }
      if (viewedAtRef.current !== null && viewedAtRef.current > startTime) {
        startSource = 'viewed';
        startTime = viewedAtRef.current;
      }

      const firstInteractionAt = firstInteractionAtRef.current;
      if (
        firstInteractionAt !== null &&
        firstInteractionAt - startTime > IDLE_GAP_THRESHOLD_MS
      ) {
        startSource = 'interaction';
        startTime = firstInteractionAt;
      }

      // Symmetric to the start-time override above.
      let inboundReviewEndSource: PendingInboundReviewTiming['inboundReviewEndSource'] =
        'continue_click';
      let inboundReviewEndTime = continuePressedAt;
      const lastInteractionAt = lastInteractionAtRef.current;
      if (
        lastInteractionAt !== null &&
        lastInteractionAt > startTime &&
        continuePressedAt - lastInteractionAt > IDLE_GAP_THRESHOLD_MS
      ) {
        inboundReviewEndSource = 'interaction';
        inboundReviewEndTime = lastInteractionAt;
      }

      // Keyed by orderUUIDs[0]: the only identifier order-summary.tsx
      // receives, and not guaranteed to equal orderUUID.
      setPendingReviewTiming(orderUUIDs[0], {
        continuePressedAt,
        inboundReviewEndSource,
        inboundReviewEndTime,
        orderUUID,
        orderUUIDs: pendingOrderUUIDs,
        restaurantUUID,
        startSource,
        startTime,
        supplierUUID,
      });
    },
    [
      orderUUID,
      orderUUIDs,
      pendingOrderUUIDs,
      restaurantUUID,
      setPendingReviewTiming,
      supplierUUID,
    ],
  );

  return { recordInteraction, stageContinuePress };
};
